import React from 'react';
import { ClientCustomProperty } from '../interfaces/Frameworks';
import { Theme } from '../Theme';
import { Button } from './Button';
import { CollapsiblePanel } from './CollapsiblePanel';

type ClientCustomPropertiesProps = {
    properties: ClientCustomProperty[];
    isEditing: boolean;
    onChange: (properties: ClientCustomProperty[]) => void;
};

// Identify names that cannot distinguish a property when displayed or included in reports.
function getPropertyNameError(properties: ClientCustomProperty[], index: number): string {
    const name = properties[index].name.trim();
    if (!name) {
        return `Enter a name for property ${index + 1} or remove it.`;
    }
    if (properties.some((property, otherIndex) => (
        otherIndex !== index && property.name.trim().toLowerCase() === name.toLowerCase()
    ))) {
        return `Use a unique name for property ${index + 1}; "${name}" is already used.`;
    }
    return '';
}

// Share the same validation with the record's Save action.
export function getCustomPropertiesError(properties: ClientCustomProperty[]): string {
    for (let index = 0; index < properties.length; index += 1) {
        const error = getPropertyNameError(properties, index);
        if (error) {
            return error;
        }
    }
    return '';
}

// Keep row identity stable while names change, including browsers without randomUUID.
function createPropertyId(): string {
    return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `property-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// Edit draft text properties; the surrounding Client Record owns Save and Discard.
export function ClientCustomProperties({ properties, isEditing, onChange }: ClientCustomPropertiesProps) {
    const sectionId = React.useId();
    if (!isEditing && properties.length === 0) {
        return null;
    }

    // Copy changed rows so edits never mutate the saved Client Record.
    const updateProperty = (id: string, field: 'name' | 'value', value: string) => {
        onChange(properties.map((property) => property.id === id ? { ...property, [field]: value } : property));
    };

    return (
        <CollapsiblePanel title='Custom properties' contentStyle={styles.panelContent}>
            {isEditing ? (
                <div style={styles.editor}>
                    {properties.length === 0 && (
                        <p style={styles.emptyState}>Add named text fields for additional information about this person.</p>
                    )}
                    {properties.map((property, index) => {
                        const nameError = getPropertyNameError(properties, index);
                        const nameId = `${sectionId}-${property.id}-name`;
                        const valueId = `${sectionId}-${property.id}-value`;
                        const errorId = `${sectionId}-${property.id}-error`;
                        return (
                            <div key={property.id} style={styles.editorRow}>
                                <div style={styles.nameField}>
                                    <label htmlFor={nameId} style={styles.label}>Property name</label>
                                    <input
                                        id={nameId}
                                        type='text'
                                        aria-label={`Property name ${index + 1}`}
                                        aria-invalid={!!nameError}
                                        aria-describedby={nameError ? errorId : undefined}
                                        value={property.name}
                                        onChange={(event) => updateProperty(property.id, 'name', event.target.value)}
                                        style={styles.input}
                                    />
                                    {nameError && <p id={errorId} role='alert' style={styles.error}>{nameError}</p>}
                                </div>
                                <div style={styles.valueField}>
                                    <label htmlFor={valueId} style={styles.label}>Value</label>
                                    <textarea
                                        id={valueId}
                                        aria-label={`Property value ${index + 1}`}
                                        rows={3}
                                        value={property.value}
                                        onChange={(event) => updateProperty(property.id, 'value', event.target.value)}
                                        style={styles.textarea}
                                    />
                                </div>
                                <button
                                    type='button'
                                    aria-label={`Remove property ${index + 1}${property.name.trim() ? `: ${property.name.trim()}` : ''}`}
                                    onClick={() => onChange(properties.filter((item) => item.id !== property.id))}
                                    style={styles.removeButton}
                                >
                                    Remove
                                </button>
                            </div>
                        );
                    })}
                    <Button
                        label='Add property'
                        type='secondary'
                        onClick={() => onChange([...properties, { id: createPropertyId(), name: '', value: '' }])}
                        style={styles.addButton}
                    />
                </div>
            ) : (
                <dl style={styles.propertyList}>
                    {properties.map((property) => (
                        <div key={property.id} style={styles.displayRow}>
                            <dt style={styles.displayName}>{property.name}</dt>
                            <dd style={styles.displayValue}>{property.value || '—'}</dd>
                        </div>
                    ))}
                </dl>
            )}
        </CollapsiblePanel>
    );
}

const styles: Record<string, React.CSSProperties> = {
    // Keep the Personal details inset in both modes, including after Save or Discard.
    panelContent: {
        paddingLeft: '32px',
        paddingRight: '24px',
    },
    editor: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
    },
    emptyState: {
        ...Theme.Fonts.body,
        margin: 0,
        color: 'rgba(0, 0, 0, 0.65)',
    },
    editorRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '16px',
        flexWrap: 'wrap',
    },
    nameField: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        flex: '0 1 170px',
        minWidth: 0,
    },
    valueField: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        flex: '1 1 220px',
        minWidth: 0,
    },
    label: {
        ...Theme.Fonts.body,
        fontWeight: 600,
        color: Theme.Colours.text,
    },
    input: {
        ...Theme.Fonts.body,
        width: '100%',
        // Clear global input margins so the box starts directly beneath its label.
        margin: 0,
        boxSizing: 'border-box',
        padding: '10px 12px',
        border: Theme.Colours.border,
        borderRadius: '6px',
        color: Theme.Colours.text,
        backgroundColor: Theme.Colours.workArea,
    },
    textarea: {
        ...Theme.Fonts.body,
        fontFamily: 'inherit',
        width: '100%',
        boxSizing: 'border-box',
        padding: '10px 12px',
        border: Theme.Colours.border,
        borderRadius: '6px',
        resize: 'vertical',
        color: Theme.Colours.text,
        backgroundColor: Theme.Colours.workArea,
    },
    error: {
        ...Theme.Fonts.body,
        margin: 0,
        color: '#8a1c12',
        overflowWrap: 'anywhere',
    },
    removeButton: {
        ...Theme.Fonts.body,
        padding: '10px 12px',
        marginTop: '24px',
        marginLeft: 0,
        border: Theme.Colours.border,
        borderRadius: '6px',
        color: '#8a1c12',
        backgroundColor: Theme.Colours.workArea,
        cursor: 'pointer',
    },
    addButton: {
        alignSelf: 'flex-start',
        marginLeft: 0,
    },
    propertyList: {
        margin: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    displayRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px 24px',
    },
    displayName: {
        ...Theme.Fonts.title2,
        fontWeight: 600,
        flex: '1 1 170px',
        minWidth: 0,
        overflowWrap: 'anywhere',
    },
    displayValue: {
        ...Theme.Fonts.body,
        margin: 0,
        flex: '3 1 220px',
        minWidth: 0,
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
    },
};
